/**
 * Invoice reminder calendar (admin → Billing).
 *
 * A shared, due-bucketed worklist of scheduled invoice runs for the whole sales
 * team + root (Phase 9). Schedule a run for a customer + date + period; the list
 * groups by overdue / today / upcoming and shows who scheduled each one and a
 * status chip. "Generate" issues the invoice from an entry and marks it
 * processed — corporate runs hold for approval (chip: Awaiting approval).
 */
import { escapeHtml } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';
import { openInvoicePreview } from './invoice-preview.js';

// Build the minimal invoice shape openInvoicePreview needs from a reminder row.
function invoiceFromReminder(it) {
  return {
    id: it.invoice_id,
    number: it.invoice_number,
    status: it.invoice_status,
    kind: 'invoice',
    pending_approval: !!it.awaiting_approval,
  };
}

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

const BUCKET_TITLES = { overdue: 'Overdue', today: 'Due today', upcoming: 'Upcoming' };
const CHIP_CLASS = {
  Overdue: 'danger', Due: 'warn', Scheduled: '', Processed: 'ok',
  Cancelled: 'muted', 'Awaiting approval': 'warn',
};

let selected = null;          // the customer picked from search: { porta_customer_id, name, segment }
let searchTimer = null;
let viewMode = 'list';        // 'list' | 'week' | 'month'
let calCursor = startOfDay(new Date());   // anchors the week/month being shown
let shownDay = null;          // the day whose strip is open (re-shown after an edit)
let cachedItems = [];         // last-loaded reminders (re-painted on view/nav change without refetch)
let cachedCounts = {};

export async function mountCalendar(container) {
  container = container || document.getElementById('invoicingCalendarView');
  if (!container) return;
  selected = null;
  container.innerHTML = `
    <div class="crm-detail-header" style="margin-bottom:10px;">
      <strong style="font-size:15px;">Invoice reminder calendar</strong>
      <span class="crm-detail-sub" style="margin-left:8px;">Search for a customer, then schedule an invoice run — the whole team shares this worklist</span>
      <button class="btn ghost" data-cal-refresh style="margin-left:auto;">↻ Refresh</button>
    </div>


    <form data-cal-form class="row" style="gap:6px; flex-wrap:wrap; align-items:flex-end; margin-bottom:12px; padding:10px; background:var(--panel-2,#f7f7f9); border-radius:8px;">
      <div class="field" style="min-width:280px; position:relative;">
        <span class="crm-detail-sub">Customer</span>
        <input data-cal-search class="input" type="text" autocomplete="off" placeholder="Search by name, phone or email…">
        <div data-cal-results class="crm-inbox-table" style="display:none; position:absolute; z-index:20; top:100%; left:0; right:0; background:#fff; border:1px solid #e2e2e2; border-radius:8px; max-height:260px; overflow:auto; box-shadow:0 6px 20px rgba(0,0,0,.12);"></div>
        <div data-cal-selected style="display:none; margin-top:6px;"></div>
      </div>
      <label class="field"><span class="crm-detail-sub">Date</span>
        <input name="scheduled_for" type="date" required class="input"></label>
      <label class="field"><span class="crm-detail-sub">Period</span>
        <select name="period" class="input">
          <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
          <option value="semiannual">6-monthly</option><option value="annual">Yearly</option></select></label>
      <button type="submit" class="btn primary" data-cal-submit disabled>Schedule</button>
    </form>

    <div data-cal-body><div class="crm-detail-loading">Loading…</div></div>`;

  container.querySelector('[data-cal-refresh]').addEventListener('click', () => load(container));
  container.querySelector('[data-cal-form]').addEventListener('submit', (ev) => onSchedule(ev, container));
  container.querySelector('[data-cal-body]').addEventListener('click', (ev) => onBodyClick(ev, container));
  wireSearch(container);
  await load(container);
}

function wireSearch(container) {
  const input = container.querySelector('[data-cal-search]');
  const results = container.querySelector('[data-cal-results]');
  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) { results.style.display = 'none'; results.innerHTML = ''; return; }
    searchTimer = setTimeout(() => runSearch(container, q), 250);
  });
  results.addEventListener('click', (ev) => {
    const row = ev.target.closest('[data-porta]');
    if (!row || row.dataset.porta === '') return;
    selectCustomer(container, {
      porta_customer_id: Number(row.dataset.porta),
      name: row.dataset.name,
      segment: row.dataset.segment || null,
    });
  });
  // dismiss the dropdown on outside click
  document.addEventListener('click', (ev) => {
    if (!container.contains(ev.target)) { results.style.display = 'none'; }
  });
}

async function runSearch(container, q) {
  const results = container.querySelector('[data-cal-results]');
  results.style.display = '';
  results.innerHTML = '<div class="crm-detail-loading" style="padding:8px;">Searching…</div>';
  try {
    const data = await api.fetch(API.crmCustomers(`search=${encodeURIComponent(q)}&status=all&limit=8`), { method: 'GET' });
    const items = data.items || [];
    if (!items.length) { results.innerHTML = '<div class="crm-detail-empty" style="padding:8px;">No matches.</div>'; return; }
    results.innerHTML = items.map((c) => {
      // Only a numeric porta id is a real PortaBilling link; placeholders
      // (manual-…/PT-…/CLI-…) are unlinked records and can't be invoiced.
      const porta = (c.porta_customer_id ?? '').toString().trim();
      const hasPorta = /^\d+$/.test(porta);
      const sub = [c.primary_phone, hasPorta ? `#${porta}` : 'no billing account']
        .filter(Boolean).join(' · ');
      const seg = c.segment ? ` <span class="badge">${escapeHtml(c.segment)}</span>` : '';
      return `<div data-porta="${hasPorta ? escapeHtml(porta) : ''}"
        data-name="${escapeHtml(c.display_name || '')}" data-segment="${escapeHtml(c.segment || '')}"
        style="padding:8px 10px; cursor:${hasPorta ? 'pointer' : 'not-allowed'}; opacity:${hasPorta ? 1 : 0.5}; border-bottom:1px solid #f0f0f0;">
        <div><strong>${escapeHtml(c.display_name || '—')}</strong>${seg}</div>
        <div class="crm-detail-sub">${escapeHtml(sub)}</div></div>`;
    }).join('');
  } catch (err) {
    results.innerHTML = '<div class="crm-detail-error" style="padding:8px;">Search unavailable.</div>';
  }
}

function selectCustomer(container, cust) {
  selected = cust;
  const input = container.querySelector('[data-cal-search]');
  const results = container.querySelector('[data-cal-results]');
  const chip = container.querySelector('[data-cal-selected]');
  input.value = '';
  results.style.display = 'none';
  results.innerHTML = '';
  const seg = cust.segment ? ` · ${escapeHtml(cust.segment)}` : '';
  chip.style.display = '';
  chip.innerHTML = `<span class="badge ok">${escapeHtml(cust.name || ('Customer ' + cust.porta_customer_id))}</span>
    <span class="crm-detail-sub">#${escapeHtml(String(cust.porta_customer_id))}${seg}</span>
    <button type="button" class="btn ghost" data-cal-clear style="padding:0 6px;">✕</button>`;
  chip.querySelector('[data-cal-clear]').addEventListener('click', () => clearSelection(container));
  container.querySelector('[data-cal-submit]').disabled = false;
}

function clearSelection(container) {
  selected = null;
  const chip = container.querySelector('[data-cal-selected]');
  chip.style.display = 'none';
  chip.innerHTML = '';
  container.querySelector('[data-cal-submit]').disabled = true;
}

async function load(container) {
  const body = container.querySelector('[data-cal-body]');
  body.innerHTML = '<div class="crm-detail-loading">Loading…</div>';
  try {
    const data = await api.fetch(API.invoicingReminders(), { method: 'GET' });
    cachedItems = data.items || [];
    cachedCounts = data.counts || {};
    setNavBadge(cachedCounts.overdue || 0);
    paint(container);
  } catch (err) {
    body.innerHTML = '<div class="crm-detail-error">Failed to load the calendar.</div>';
  }
}

// ---- date helpers (local time; scheduled_for is a plain YYYY-MM-DD) ----------
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfWeek(d) { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); } // Monday
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function byDay(items) {
  const map = new Map();
  for (const it of items) {
    if (!it.scheduled_for) continue;
    if (!map.has(it.scheduled_for)) map.set(it.scheduled_for, []);
    map.get(it.scheduled_for).push(it);
  }
  return map;
}

// ---- top toolbar: view toggle + navigator -----------------------------------
function toolbar() {
  const btn = (mode, label) =>
    `<button class="btn ${viewMode === mode ? 'primary' : 'ghost'}" data-view="${mode}" style="font-size:12px; padding:3px 10px;">${label}</button>`;
  let rangeLabel = '';
  if (viewMode === 'week') {
    const s = startOfWeek(calCursor); const e = addDays(s, 6);
    rangeLabel = `${s.getDate()} ${MONTHS[s.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`;
  } else if (viewMode === 'month') {
    rangeLabel = `${MONTHS[calCursor.getMonth()]} ${calCursor.getFullYear()}`;
  }
  const nav = viewMode === 'list' ? '' : `
    <div class="row" style="gap:4px; align-items:center; margin-left:auto;">
      <button class="btn ghost" data-nav="prev" title="Previous">‹</button>
      <button class="btn ghost" data-nav="today">Today</button>
      <button class="btn ghost" data-nav="next" title="Next">›</button>
      <strong style="margin-left:8px; min-width:150px; text-align:right;">${escapeHtml(rangeLabel)}</strong>
    </div>`;
  return `<div class="row" style="gap:6px; align-items:center; margin-bottom:10px;">
    ${btn('list', 'List')} ${btn('week', 'Week')} ${btn('month', 'Month')} ${nav}</div>`;
}

function paint(container) {
  const body = container.querySelector('[data-cal-body]');
  let view;
  if (viewMode === 'week') view = renderWeek();
  else if (viewMode === 'month') view = renderMonth();
  else view = renderList();
  const dayDetail = viewMode === 'list' ? '' : '<div data-cal-day style="margin-top:12px;"></div>';
  body.innerHTML = toolbar() + view + dayDetail;
}

// ---- day cell (shared by week + month) --------------------------------------
function dayCell(date, items, opts = {}) {
  const iso = isoOf(date);
  const isToday = iso === isoOf(new Date());
  const pills = (items || []).slice(0, opts.max || 4).map((it) =>
    `<div class="cal-pill badge ${CHIP_CLASS[it.chip] || ''}" title="${escapeHtml((it.customer_name || '') + ' · ' + it.chip)}"
       style="display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; text-align:left; margin-top:2px; cursor:pointer;">
       ${escapeHtml(it.customer_name || ('Customer ' + it.porta_customer_id))}</div>`).join('');
  const more = items && items.length > (opts.max || 4)
    ? `<div class="crm-detail-sub" style="margin-top:2px;">+${items.length - (opts.max || 4)} more</div>` : '';
  const clickable = items && items.length ? `data-day="${iso}" style="cursor:pointer;"` : '';
  const dim = opts.dim ? 'opacity:.45;' : '';
  return `<div class="cal-cell" ${clickable} style="border:1px solid var(--border-soft,#e2e2e2); border-radius:8px; padding:5px 6px; min-height:${opts.minH || 78}px; ${dim} ${isToday ? 'box-shadow: inset 0 0 0 2px var(--brand,#E32213);' : ''}">
    <div class="crm-detail-sub" style="font-weight:600;">${opts.label != null ? escapeHtml(opts.label) : date.getDate()}</div>
    ${pills}${more}</div>`;
}

function gridWrap(headerCells, cells) {
  return `<div style="display:grid; grid-template-columns:repeat(7,1fr); gap:4px;">
    ${headerCells.map((h) => `<div class="crm-detail-sub" style="text-align:center; font-weight:600; padding:2px 0;">${h}</div>`).join('')}
    ${cells.join('')}</div>`;
}

function renderWeek() {
  const map = byDay(cachedItems);
  const s = startOfWeek(calCursor);
  const cells = [];
  for (let i = 0; i < 7; i++) {
    const d = addDays(s, i);
    cells.push(dayCell(d, map.get(isoOf(d)), { minH: 120, max: 8 }));
  }
  return gridWrap(DOW, cells);
}

function renderMonth() {
  const map = byDay(cachedItems);
  const first = new Date(calCursor.getFullYear(), calCursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  // Fixed 6-week grid (42 cells) — standard month layout; outside days are dimmed.
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    const outside = d.getMonth() !== calCursor.getMonth();
    cells.push(dayCell(d, map.get(isoOf(d)), { minH: 78, max: 3, dim: outside }));
  }
  return gridWrap(DOW, cells);
}

function renderList() {
  const items = cachedItems;
  if (!items.length) {
    return '<div class="crm-detail-empty">No scheduled invoice runs yet. Use the form above to schedule one.</div>';
  }
  const byBucket = { overdue: [], today: [], upcoming: [] };
  const processed = [];
  const cancelled = [];
  for (const it of items) {
    if (it.status === 'processed') processed.push(it);
    else if (it.status === 'cancelled') cancelled.push(it);
    else if (byBucket[it.bucket]) byBucket[it.bucket].push(it);
  }
  const overdueNote = cachedCounts.overdue ? '<span class="crm-detail-sub">— needs attention</span>' : '';
  return section(BUCKET_TITLES.overdue, byBucket.overdue, overdueNote) +
    section(BUCKET_TITLES.today, byBucket.today) +
    section(BUCKET_TITLES.upcoming, byBucket.upcoming) +
    section('Processed', processed) +
    section('Cancelled', cancelled);
}

// Show one day's reminders (with actions) below the grid.
function showDay(container, iso) {
  const strip = container.querySelector('[data-cal-day]');
  if (!strip) return;
  shownDay = iso;
  const items = byDay(cachedItems).get(iso) || [];
  if (!items.length) { strip.innerHTML = ''; return; }
  strip.innerHTML = `<div class="crm-detail-sub" style="text-transform:uppercase; letter-spacing:.5px; font-weight:600; margin-bottom:4px;">
      ${escapeHtml(iso)} <span class="badge">${items.length}</span></div>
    <div class="table-wrap"><table class="crm-inbox-table"><tbody>${items.map(rowHtml).join('')}</tbody></table></div>`;
}

function onBodyClick(ev, container) {
  const viewBtn = ev.target.closest('[data-view]');
  if (viewBtn) { viewMode = viewBtn.dataset.view; paint(container); return; }
  const navBtn = ev.target.closest('[data-nav]');
  if (navBtn) {
    const dir = navBtn.dataset.nav;
    if (dir === 'today') calCursor = startOfDay(new Date());
    else { const step = dir === 'next' ? 1 : -1; calCursor = viewMode === 'week' ? addDays(calCursor, 7 * step) : addMonths(calCursor, step); }
    paint(container);
    return;
  }
  const dayEl = ev.target.closest('[data-day]');
  if (dayEl) { showDay(container, dayEl.dataset.day); return; }
  onRowClick(ev, container);   // generate / cancel on a row
}

function chip(text) {
  return `<span class="badge ${CHIP_CLASS[text] || ''}">${escapeHtml(text)}</span>`;
}

function rowHtml(it) {
  const canAct = it.status === 'scheduled';
  const who = it.scheduled_by ? `<span class="crm-detail-sub">by ${escapeHtml(it.scheduled_by)}</span>` : '';
  const link = it.invoice_number
    ? `<span class="crm-detail-sub">→ ${escapeHtml(it.invoice_number)}</span>` : '';
  const actions = canAct
    ? `<button class="btn primary" data-generate>Generate</button>
       <button class="btn ghost" data-edit>Edit</button>
       <button class="btn ghost" data-cancel>Cancel</button>`
    : (it.invoice_id ? `<button class="btn" data-preview>Preview</button>` : '');
  return `<tr data-id="${escapeHtml(it.id)}">
    <td><strong>${escapeHtml(it.customer_name || ('Customer ' + it.porta_customer_id))}</strong>
        <div class="crm-detail-sub">#${escapeHtml(String(it.porta_customer_id))} · ${escapeHtml(it.segment || 'home')} · ${escapeHtml(it.period)}</div></td>
    <td>${escapeHtml(it.scheduled_for)} ${who}</td>
    <td>${chip(it.chip)} ${link}</td>
    <td style="white-space:nowrap; text-align:right;">${actions}</td></tr>`;
}

function section(title, items, extraNote) {
  if (!items.length) return '';
  return `<div style="margin-bottom:14px;">
    <div class="crm-detail-sub" style="text-transform:uppercase; letter-spacing:.5px; font-weight:600; margin-bottom:4px;">
      ${escapeHtml(title)} <span class="badge">${items.length}</span> ${extraNote || ''}</div>
    <div class="table-wrap"><table class="crm-inbox-table"><tbody>
      ${items.map(rowHtml).join('')}
    </tbody></table></div></div>`;
}

async function onSchedule(ev, container) {
  ev.preventDefault();
  const f = ev.currentTarget;
  if (!selected) { flash('Search for and select a customer first.', 'error'); return; }
  if (!f.scheduled_for.value) { flash('Pick a date.', 'error'); return; }
  const payload = {
    porta_customer_id: selected.porta_customer_id,
    customer_name: selected.name || null,
    segment: selected.segment || null,
    scheduled_for: f.scheduled_for.value,
    period: f.period.value,
  };
  try {
    await api.fetch(API.invoicingReminderCreate(), { method: 'POST', body: JSON.stringify(payload) });
    flash('Invoice run scheduled.', 'success');
    clearSelection(container);
    f.scheduled_for.value = '';
    await load(container);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Could not schedule.', 'error');
  }
}

async function onRowClick(ev, container) {
  if (ev.target.closest('[data-edit-form]')) return;   // clicks inside the editor
  const row = ev.target.closest('tr[data-id]');
  if (!row) return;
  const id = row.dataset.id;
  if (ev.target.closest('[data-generate]')) await generate(id, container);
  else if (ev.target.closest('[data-cancel]')) await cancel(id, container);
  else if (ev.target.closest('[data-edit]')) {
    const it = cachedItems.find((x) => String(x.id) === String(id));
    if (it) openEditRow(row, it, container);
  } else if (ev.target.closest('[data-preview]')) {
    const it = cachedItems.find((x) => String(x.id) === String(id));
    if (it && it.invoice_id) openInvoicePreview(invoiceFromReminder(it), { onChanged: () => reloadCalendar(container) });
  }
}

// Reload data and repaint the list/grid + the open day strip.
async function reloadCalendar(container) {
  await load(container);
  if (shownDay) showDay(container, shownDay);
}

// Inline editor row for a still-scheduled run (date / period / note).
function openEditRow(row, it, container) {
  const next = row.nextElementSibling;
  if (next && next.dataset.editFor === String(it.id)) { next.remove(); return; }  // toggle off
  const open = row.parentElement.querySelector('tr[data-edit-for]');
  if (open) open.remove();
  const tr = document.createElement('tr');
  tr.dataset.editFor = String(it.id);
  tr.innerHTML = `<td colspan="4" style="background:var(--panel-2,#f7f7f9);">
    <form data-edit-form class="row" style="gap:8px; align-items:flex-end; flex-wrap:wrap; padding:8px 4px;">
      <label class="field"><span class="crm-detail-sub">Date</span>
        <input name="scheduled_for" type="date" class="input" value="${escapeHtml(it.scheduled_for || '')}"></label>
      <label class="field"><span class="crm-detail-sub">Period</span>
        <select name="period" class="input">
          <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
          <option value="semiannual">6-monthly</option><option value="annual">Yearly</option></select></label>
      <label class="field" style="flex:1; min-width:200px;"><span class="crm-detail-sub">Note</span>
        <input name="note" type="text" class="input" value="${escapeHtml(it.note || '')}" placeholder="Optional"></label>
      <button type="submit" class="btn primary">Save</button>
      <button type="button" class="btn ghost" data-edit-cancel>Cancel</button>
    </form></td>`;
  tr.querySelector('select[name="period"]').value = it.period || 'monthly';
  row.after(tr);
  tr.querySelector('[data-edit-cancel]').addEventListener('click', () => tr.remove());
  tr.querySelector('[data-edit-form]').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const f = ev.currentTarget;
    if (!f.scheduled_for.value) { flash('Pick a date.', 'error'); return; }
    try {
      await api.fetch(API.invoicingReminderUpdate(it.id), {
        method: 'PATCH',
        body: JSON.stringify({ scheduled_for: f.scheduled_for.value, period: f.period.value, note: f.note.value.trim() || null }),
      });
      flash('Schedule updated.', 'success');
      await reloadCalendar(container);
    } catch (err) {
      flash(api.formatErrorDetail(err) || 'Could not update the schedule.', 'error');
    }
  });
}

// Ask whether to add VAT before generating: a checkbox that reveals a % field.
// Resolves { cancelled } or { cancelled:false, vat_rate }.
function askVatOnGenerate() {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.style.cssText = 'border:0; border-radius:10px; padding:0; max-width:340px; width:92%;';
    dlg.innerHTML = `
      <form method="dialog" style="padding:16px; font-size:13px;">
        <div style="font-weight:600; margin-bottom:10px;">Generate the invoice</div>
        <label style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
          <input type="checkbox" data-vat-on> Add VAT
        </label>
        <label class="field" data-vat-field style="display:none; margin-bottom:12px;">
          <span class="crm-detail-sub">VAT % (charged on the net subscription months only — PortaBilling balances already include VAT)</span>
          <input data-vat-rate type="number" step="0.01" min="0" max="100" class="input" placeholder="0">
          <span class="crm-detail-sub" style="margin-top:8px;">How it is shown</span>
          <select data-vat-mode class="input">
            <option value="exclusive">Added on top of the prices</option>
            <option value="inclusive">Included in the prices</option>
          </select>
        </label>
        <label style="display:flex; align-items:center; gap:8px; margin-bottom:12px;">
          <input type="checkbox" data-expand checked> Itemise each connection
          <span class="crm-detail-sub" style="margin-left:auto;">uncheck to combine into one line</span>
        </label>
        <div style="display:flex; gap:8px; justify-content:flex-end;">
          <button type="button" class="btn ghost" data-cancel>Cancel</button>
          <button type="submit" class="btn primary">Generate</button>
        </div>
      </form>`;
    document.body.appendChild(dlg);
    const on = dlg.querySelector('[data-vat-on]');
    const field = dlg.querySelector('[data-vat-field]');
    const rate = dlg.querySelector('[data-vat-rate]');
    const mode = dlg.querySelector('[data-vat-mode]');
    const expand = dlg.querySelector('[data-expand]');
    const done = (result) => { try { dlg.close(); } catch (e) { /* not open */ } dlg.remove(); resolve(result); };
    on.addEventListener('change', () => { field.style.display = on.checked ? '' : 'none'; if (on.checked) rate.focus(); });
    dlg.querySelector('[data-cancel]').addEventListener('click', () => done({ cancelled: true }));
    dlg.querySelector('form').addEventListener('submit', (ev) => {
      ev.preventDefault();
      const v = on.checked && rate.value !== '' ? rate.value : null;
      if (on.checked && !v) { rate.focus(); return; }
      done({ cancelled: false, vat_rate: v, vat_mode: (v && mode) ? mode.value : 'exclusive',
             expand_connections: expand ? expand.checked : true });
    });
    dlg.addEventListener('cancel', (ev) => { ev.preventDefault(); done({ cancelled: true }); });
    try { dlg.showModal(); } catch (e) { done({ cancelled: false, vat_rate: null, vat_mode: 'exclusive', expand_connections: true }); }
  });
}

async function generate(id, container) {
  const opts = await askVatOnGenerate();
  if (opts.cancelled) return;
  try {
    // Generate but do NOT auto-send — mirror the customer-details view: review it in
    // the preview first, then send (or, when it needs approval, approve) from there.
    const res = await api.fetch(API.invoicingReminderGenerate(id),
                               { method: 'POST', body: JSON.stringify({ send: false, vat_rate: opts.vat_rate, vat_mode: opts.vat_mode || 'exclusive', expand_connections: opts.expand_connections }) });
    const held = res.delivery && res.delivery.held === 'awaiting_approval';
    flash(held ? 'Invoice generated — awaiting approval before it can be sent or downloaded.' : 'Invoice generated.', 'success');
    await load(container);
    if (res.invoice && res.invoice.id) {
      openInvoicePreview(res.invoice, { onChanged: () => load(container) });
    }
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Generation failed', 'error');
  }
}

async function cancel(id, container) {
  if (!window.confirm('Cancel this scheduled run?')) return;
  try {
    await api.fetch(API.invoicingReminderCancel(id), { method: 'DELETE' });
    flash('Run cancelled.', 'success');
    await load(container);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Could not cancel', 'error');
  }
}

function setNavBadge(n) {
  const badge = document.getElementById('invoicingCalendarBadge');
  if (!badge) return;
  badge.textContent = n > 0 ? String(n) : '';
  badge.style.display = n > 0 ? '' : 'none';
}

export async function refreshBadge() {
  try {
    const data = await api.fetch(API.invoicingReminders('due=overdue'), { method: 'GET' });
    setNavBadge((data.counts || {}).overdue || 0);
  } catch (err) { /* non-fatal */ }
}

export default { mountCalendar, refreshBadge };
